/**
 * When a trophy or story beat actually fires.
 *
 * Detection lives here, on the client, against the diary it already has —
 * `packages/shared/src/trophies.ts` only knows what an award is called, never
 * when it is earned. Three kinds: counters that fall out of numbers the caller
 * already holds, one that only needs the account's age, and the rest that need
 * the diary re-read.
 *
 * One pass over the live entries answers every history question at once. That
 * is a full-table read, so it is deliberately not run on every log — see
 * `checkAwards`, which skips the scan once every award that depends on it has
 * been granted. For any given account that happens once and then never again.
 *
 * The second batch of awards is about the parts of a diary the first batch
 * ignored: what was drunk, which weekends held, how long somebody stayed on
 * the scale, and whether they were here on the second of November.
 */

import {
  addDays,
  daysBetween,
  deviceTimeZone,
  HUGE_PORTION_KCAL,
  localDateString,
  MIDNIGHT_HOURS,
  NTH_SNACK,
  TROPHIES,
  WATER_NAME,
} from '@gramas/shared'
import { db } from '../db/dexie.js'
import { bodyMeasurementsBetween, grantAward, hasAward } from '../db/repo.js'

interface Facts {
  totalLogs: number
  streakDays: number
  /** Entries written between 00:00 and 04:00 local, lifetime. */
  midnightLogs: number
  /** Entries of `HUGE_PORTION_KCAL` or more, lifetime. */
  hugePortions: number
  /** Snacks that were the fourth or later of their day, lifetime. */
  lateSnacks: number
  /** Whole years since the account was opened; 0 when the date is unknown. */
  accountYears: number
  /** Days that reached the water mark, lifetime. */
  waterDays: number
  /** Consecutive whole weekends — both days logged — ending most recently. */
  weekendRun: number
  /** Days with an entry at all, lifetime. Not consecutive; just days. */
  loggedDays: number
  /** Days weighed in over the trailing four weeks. */
  weighInsInMonth: number
  /** Whether any entry has ever been made on the second of November. */
  loggedOnVelines: boolean
  /** Days since the most recent after-midnight entry; Infinity if never. */
  daysSinceMidnightLog: number
}

const DETECTABLE: ReadonlyArray<{ key: string; met: (facts: Facts) => boolean }> = [
  { key: 'beat.log-1', met: (f) => f.totalLogs >= 1 },
  { key: 'beat.log-100', met: (f) => f.totalLogs >= 100 },
  { key: 'trophy.log-1000', met: (f) => f.totalLogs >= 1000 },
  { key: 'trophy.streak-7', met: (f) => f.streakDays >= 7 },
  { key: 'beat.streak-30', met: (f) => f.streakDays >= 30 },
  { key: 'trophy.streak-100', met: (f) => f.streakDays >= 100 },
  { key: 'trophy.midnight-5', met: (f) => f.midnightLogs >= 5 },
  { key: 'trophy.huge-portion-10', met: (f) => f.hugePortions >= 10 },
  { key: 'trophy.nth-snack-25', met: (f) => f.lateSnacks >= 25 },
  { key: 'beat.year-1', met: (f) => f.accountYears >= 1 },
  { key: 'beat.water-7', met: (f) => f.waterDays >= 7 },
  { key: 'trophy.water-50', met: (f) => f.waterDays >= 50 },
  { key: 'trophy.weekend-4', met: (f) => f.weekendRun >= 4 },
  { key: 'trophy.patience-28', met: (f) => f.weighInsInMonth >= WEIGH_IN_DAYS },
  { key: 'beat.velines-1', met: (f) => f.loggedOnVelines },
  { key: 'beat.days-365', met: (f) => f.loggedDays >= 365 },
  {
    // Earned by not doing something, so it can only be checked against
    // somebody who did it in the first place — otherwise every account that
    // has never once logged at 2 a.m. would be handed a reformation trophy
    // for a habit it never had.
    key: 'trophy.night-owl-reform',
    met: (f) => f.midnightLogs >= 5 && f.daysSinceMidnightLog >= NIGHT_OWL_CLEAN_DAYS,
  },
]

/**
 * The keys whose evidence only exists in a full-table scan. Once all of them
 * are in the cabinet the scan is skipped entirely, which for any given account
 * happens once and then never again.
 *
 * `trophy.night-owl-reform` is deliberately not in this list even though it
 * reads scanned data: it is the one award whose condition can become true
 * again after being false, so its evidence has to keep being gathered until it
 * is actually granted.
 */
const SCAN_KEYS = [
  'trophy.midnight-5',
  'trophy.huge-portion-10',
  'trophy.nth-snack-25',
  'trophy.water-50',
  'trophy.weekend-4',
  'beat.days-365',
  'beat.velines-1',
  'trophy.night-owl-reform',
] as const

/** Millilitres in a day that count as the day's water having been drunk. */
const WATER_DAY_ML = 2000
/** Days on the scale inside four weeks that count as having been patient. */
const WEIGH_IN_DAYS = 12
/** A month clean of after-midnight entries is a reformed night shift. */
const NIGHT_OWL_CLEAN_DAYS = 30

export interface HistoryCounts {
  midnightLogs: number
  hugePortions: number
  lateSnacks: number
  waterDays: number
  weekendRun: number
  loggedDays: number
  loggedOnVelines: boolean
  daysSinceMidnightLog: number
}

const NO_HISTORY: HistoryCounts = {
  midnightLogs: 0,
  hugePortions: 0,
  lateSnacks: 0,
  waterDays: 0,
  weekendRun: 0,
  loggedDays: 0,
  loggedOnVelines: false,
  daysSinceMidnightLog: Number.POSITIVE_INFINITY,
}

/**
 * One pass, three counts.
 *
 * `loggedAt` is the wall-clock moment the entry was written and is what the
 * midnight trigger is defined against — not `logDate`, which a 01:00 snack may
 * well have backdated to the evening it belonged to. An entry with no
 * `loggedAt` (imported, or written before the column existed) simply does not
 * count toward the night shift rather than being guessed at.
 */
export async function historyCounts(today: string = todayLocal()): Promise<HistoryCounts> {
  const counts: HistoryCounts = { ...NO_HISTORY }
  const snacksPerDay = new Map<string, number>()
  const waterPerDay = new Map<string, number>()
  const loggedDays = new Set<string>()
  let lastMidnightDay: string | null = null

  await db.logEntries.each((entry) => {
    if (entry.deletedAt != null) return
    // Water is a zero-calorie row, so it has to be counted before the
    // kcal filter every other question here wants.
    if (entry.snapshotName === WATER_NAME) {
      waterPerDay.set(entry.logDate, (waterPerDay.get(entry.logDate) ?? 0) + entry.quantityG)
      return
    }
    if (entry.kcal <= 0) return

    loggedDays.add(entry.logDate)
    if (entry.logDate.slice(5) === VELINES_MMDD) counts.loggedOnVelines = true
    if (entry.loggedAt != null) {
      const hour = new Date(entry.loggedAt).getHours()
      if (hour >= MIDNIGHT_HOURS[0] && hour < MIDNIGHT_HOURS[1]) {
        counts.midnightLogs++
        if (lastMidnightDay == null || entry.logDate > lastMidnightDay) {
          lastMidnightDay = entry.logDate
        }
      }
    }
    if (entry.kcal >= HUGE_PORTION_KCAL) counts.hugePortions++
    if (entry.slot === 'snack') {
      const soFar = (snacksPerDay.get(entry.logDate) ?? 0) + 1
      snacksPerDay.set(entry.logDate, soFar)
      // The fourth snack of a day is the one that stops being an accident;
      // every one after it counts too, which is what makes 25 a collection.
      if (soFar >= NTH_SNACK) counts.lateSnacks++
    }
  })

  counts.loggedDays = loggedDays.size
  for (const ml of waterPerDay.values()) if (ml >= WATER_DAY_ML) counts.waterDays++
  counts.daysSinceMidnightLog =
    lastMidnightDay == null ? Number.POSITIVE_INFINITY : daysBetween(lastMidnightDay, today)
  counts.weekendRun = weekendRunFrom(loggedDays, today)
  return counts
}

/** The second of November, as it appears in an ISO date. */
const VELINES_MMDD = '11-02'

function todayLocal(): string {
  return localDateString(new Date(), deviceTimeZone())
}

/**
 * How many whole weekends in a row, counting back, have both days logged.
 *
 * Weekends are where food diaries actually die — the week holds because the
 * week has a shape, and then Saturday arrives. Counting back from the most
 * recently *finished* weekend rather than from today, so a Saturday that has
 * not happened yet cannot break a run that is still intact.
 */
function weekendRunFrom(loggedDays: ReadonlySet<string>, today: string): number {
  // Walk back to the Sunday of the last weekend that is fully in the past.
  let cursor = today
  for (let i = 0; i < 7; i++) {
    const day = new Date(`${cursor}T12:00:00`).getDay()
    if (day === 0 && cursor !== today) break
    cursor = addDays(cursor, -1)
  }
  let run = 0
  for (let week = 0; week < 60; week++) {
    const sunday = addDays(cursor, -7 * week)
    const saturday = addDays(sunday, -1)
    if (!loggedDays.has(sunday) || !loggedDays.has(saturday)) break
    run++
  }
  return run
}

async function totalLogCount(): Promise<number> {
  return db.logEntries.filter((e) => e.deletedAt == null && e.kcal > 0).count()
}

/**
 * Days with a weight on them in the last four weeks.
 *
 * The patience trophy is not for losing anything — it is for staying on the
 * scale long enough that the trend line means something. `weightTrendPerWeek`
 * refuses to answer under three points spanning a fortnight for exactly that
 * reason, and this is the same idea rewarded rather than enforced.
 */
async function weighInsInMonth(today: string = todayLocal()): Promise<number> {
  const rows = await bodyMeasurementsBetween(addDays(today, -27), today)
  return rows.filter((row) => row.weightKg != null).length
}

/** Whole years between the account's first day and now. Null dates never celebrate. */
export function accountYears(createdAt: string | null, now: Date = new Date()): number {
  if (createdAt == null) return 0
  const opened = new Date(createdAt)
  if (Number.isNaN(opened.getTime())) return 0
  const years = now.getFullYear() - opened.getFullYear()
  const beforeAnniversary =
    now.getMonth() < opened.getMonth() ||
    (now.getMonth() === opened.getMonth() && now.getDate() < opened.getDate())
  return Math.max(0, beforeAnniversary ? years - 1 : years)
}

/**
 * Run after a log commit, and once on app open.
 *
 * `streakDays` is passed in rather than recomputed here — the caller already
 * has it, and a second streak query for the same request would just be waste.
 * Returns the awards granted by this call, newest rule first, so the caller
 * can say something about them; an empty array is the overwhelmingly common
 * answer and costs one indexed count.
 */
export async function checkAwards(
  streakDays: number,
  createdAt: string | null = null,
): Promise<string[]> {
  // The scan is skipped once its three trophies are all in the cabinet, which
  // is the steady state for any account that has had them a while.
  const scanned = await Promise.all(SCAN_KEYS.map((key) => hasAward(key)))
  const history = scanned.every(Boolean) ? NO_HISTORY : await historyCounts()

  const facts: Facts = {
    totalLogs: await totalLogCount(),
    streakDays,
    accountYears: accountYears(createdAt),
    weighInsInMonth: await weighInsInMonth(),
    ...history,
  }

  const granted: string[] = []
  for (const check of DETECTABLE) {
    if (!check.met(facts)) continue
    if (await hasAward(check.key)) continue
    const def = TROPHIES.find((t) => t.key === check.key)
    if (def == null) continue
    const row = await grantAward({ kind: def.kind, awardKey: def.key })
    if (row != null) granted.push(def.key)
  }
  return granted
}
